import { Router } from "@frui.ts/screens";
import type { EntityNames } from "config";
import { Modules } from "config";
import { action, computed } from "mobx";
import ConfirmationService from "services/confirmationService";
import EnumService from "services/enumService";
import EventBus, {
  callCenterViewModelName,
  totalCountCharges,
  totalCountExemptions,
  totalCountMessages,
  totalCountVehicleRegister,
  vehicleSearchStatus,
  vehicleSynchronizationOpenDetailRSV,
  vehicleSynchronizationOpenSyncRSV,
} from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import SecurityService from "services/securityService";
import CallCenterListViewModel from "../callCenterListViewModel";
import PriceListService from "services/priceListService";
import ExemptionRepository from "repositories/exemptionRepository";
import { trimObjectStrings } from "utils/filters";
import EmailMessageRepository from "repositories/emailMessageRepository";
import VehicleRegisterVehicle from "entities/vehicleRegisterVehicleResponse";
import type SearchVehicleRegisterVehiclesRequest from "entities/searchVehicleRegisterVehiclesRequest";
import VehicleRegisterRepository from "repositories/vehicleRegisterRepository";
import ChargesRepository from "repositories/chargesRepository";
import VehicleRegisterVehicleStatus, { vehicleRegisterVehicleStatusToVariant } from "entities/vehicleRegisterVehicleStatus";
import type VehicleRegisterVehicleClassifiedFuelType from "entities/vehicleRegisterVehicleClassifiedFuelType";
import type VehicleRegisterDetailViewModel from "./vehicleRegisterDetail/vehicleRegisterDetailViewModel";
import camelCase from "lodash/camelCase";
import type VehicleRegisterVehicleSearchResponse from "entities/vehicleRegisterVehicleSearchResponse";
import CallCenterModelNamesEnum from "entities/callCenterModelNamesEnum";
import type VehicleSynchronizationViewModel from "./vehicleSynchronization/vehicleSynchronizationViewModel";

export const vehicleRegisterNavigationName = CallCenterModelNamesEnum.VehicleRegisterVehicle;

export enum VehicleRegisterRowColor {
  RED = "danger",
  GRAY = "gray",
  GREEN = "success",
}

export enum VehicleRegisterAction {
  VEHICLE_REGISTER_DETAIL_ACCESS = "VehicleRegisterDetailAccess",
  VEHICLE_SYNCHRONIZATION = "VehicleSynchronization",
}

@Router.registerRoute({ route: vehicleRegisterNavigationName, name: Router.Self })
export default class VehicleRegisterViewModel extends CallCenterListViewModel<
  VehicleRegisterVehicle,
  SearchVehicleRegisterVehiclesRequest
> {
  protected readonly modelName: EntityNames = "vehicleRegisterVehicle";
  readonly idProperty = "searchId";

  constructor(
    protected eventBus: EventBus,
    protected localizationService: LocalizationService,
    protected priceListService: PriceListService,
    protected enumService: EnumService,
    protected securityService: SecurityService,
    protected repository: VehicleRegisterRepository,
    protected chargesRepository: ChargesRepository,
    protected exemptionRepository: ExemptionRepository,
    protected emailMessageRepository: EmailMessageRepository,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    private vehicleRegisterDetailVMFactory: ReturnType<typeof VehicleRegisterDetailViewModel.Factory>,
    private vehicleSynchronizationVMFactory: ReturnType<typeof VehicleSynchronizationViewModel.Factory>
  ) {
    super();
    this.navigationName = vehicleRegisterNavigationName;
  }

  protected onInitialize(): Promise<any> | void {
    this.unsubscriptions.push(
      this.eventBus.subscribe(vehicleSynchronizationOpenDetailRSV, this.onVehicleSynchronizationOpenDetailRSV),
      this.eventBus.subscribe(vehicleSynchronizationOpenSyncRSV, this.onVehicleSynchronizationOpenSyncRSV)
    );
    return super.onInitialize();
  }

  protected onActivate() {
    this.eventBus.publish(callCenterViewModelName({ callCenterViewModelName: CallCenterModelNamesEnum.VehicleRegisterVehicle }));

    return super.onActivate();
  }

  @computed
  get name() {
    return this.localizationService.translateModule(Modules.callCenter, "vehicleRegister.name");
  }

  @computed
  get actions() {
    return Object.values(VehicleRegisterAction).filter(action => this.allowToAction(action));
  }

  @action.bound
  rowColor(vehicleStatus?: VehicleRegisterVehicleStatus): string {
    if (!vehicleStatus) {
      return "row-white";
    }
    const color = vehicleRegisterVehicleStatusToVariant(vehicleStatus);
    switch (color) {
      case VehicleRegisterRowColor.GRAY: {
        return "row-darkgray";
      }
      case VehicleRegisterRowColor.RED:
        return "row-red";
      case VehicleRegisterRowColor.GREEN: {
        return "row-green";
      }
      default:
        return "row-white";
    }
  }

  @action.bound
  protected async loadItems() {
    this.appliedFilter = trimObjectStrings(this.appliedFilter);
    const { licensePlate, countryId } = this.appliedFilter;
    const result = (await this.repository.getAll(
      { licensePlate: licensePlate, countryId: countryId },
      this.pagingFilter,
      this.abortController?.signal
    )) as unknown as VehicleRegisterVehicleSearchResponse;
    this.publishTotalCount(result.metadata.total);
    this.eventBus.publish(vehicleSearchStatus({ vehicleSearchStatus: result.searchStatus }));

    this.abortController = undefined;

    const items = result.items.map(item => this.enumService.proxyObjectEnums(item));
    this.setData([
      items,
      {
        limit: result.metadata.limit,
        offset: result.metadata.offset,
        totalItems: result.metadata.total,
      },
    ]);
  }

  @action.bound
  protected async loadRelatedCounts() {
    const { licensePlate, officialRecordId, countryId, orderIdent, chargeId, chargeOrderId } = this.appliedFilter;

    if (licensePlate || officialRecordId || (countryId && officialRecordId) || (countryId && licensePlate)) {
      const filter = {
        licensePlate: licensePlate,
        countryId: countryId,
      };
      const { totalCount } = await this.exemptionRepository.totalCount(filter);
      this.eventBus.publish(totalCountExemptions({ totalCountExemptions: totalCount }));
    } else {
      this.eventBus.publish(totalCountExemptions({ totalCountExemptions: 0 }));
    }

    const { totalCount } = await this.chargesRepository.totalCount(this.appliedFilter);
    this.eventBus.publish(totalCountCharges({ totalCountCharges: totalCount ?? 0 }));

    if (
      (countryId && (chargeId || licensePlate || orderIdent || chargeOrderId)) ||
      chargeId ||
      licensePlate ||
      orderIdent ||
      chargeOrderId
    ) {
      const { totalCount } = await this.emailMessageRepository.totalCount(this.appliedFilter);
      this.eventBus.publish(totalCountMessages({ totalCountMessages: totalCount }));
    } else {
      this.eventBus.publish(totalCountMessages({ totalCountMessages: 0 }));
    }
  }

  @action.bound
  protected publishTotalCount(totalCount: number) {
    this.eventBus.publish(totalCountVehicleRegister({ totalCountVehicleRegister: totalCount }));
  }

  @action
  protected onLoadItemsFailed() {
    this.eventBus.publish(vehicleSearchStatus({ vehicleSearchStatus: null }));
    super.onLoadItemsFailed();
  }

  @action.bound
  protected resetRelatedCounts() {
    this.eventBus.publish(totalCountExemptions({ totalCountExemptions: 0 }));
    this.eventBus.publish(totalCountCharges({ totalCountCharges: 0 }));
    this.eventBus.publish(totalCountMessages({ totalCountMessages: 0 }));
  }

  @action.bound
  allowToAction(action: VehicleRegisterAction) {
    if (action === VehicleRegisterAction.VEHICLE_REGISTER_DETAIL_ACCESS) {
      return this.securityService.allowTo("callcenter", "read");
    }
    if (action === VehicleRegisterAction.VEHICLE_SYNCHRONIZATION) {
      return this.securityService.allowTo("callcenter", "write");
    }
  }

  @action.bound
  allowToVehicleRegisterDetailAccess(item: VehicleRegisterVehicle) {
    return item.permittedActions.vehicleDetailAccess.isAllowed;
  }

  @action.bound
  private onVehicleSynchronizationOpenDetailRSV(event: ReturnType<typeof vehicleSynchronizationOpenDetailRSV>) {
    const item = this.items.find(item => item.vehicleId === event.payload.vehicleId);

    if (item && event.payload.openDetail) {
      this.onVehicleRegisterDetailAccessClick(item);
    }
  }

  @action.bound
  private onVehicleSynchronizationOpenSyncRSV(event: ReturnType<typeof vehicleSynchronizationOpenSyncRSV>) {
    if (event.payload.vehicleRegister && event.payload.openSync) {
      this.onVehicleSynchronizationClick(event.payload.vehicleRegister, true);
    }
  }

  @action.bound
  onVehicleRegisterDetailAccessClick(item: VehicleRegisterVehicle) {
    if (
      this.allowToVehicleRegisterDetailAccess(item) &&
      this.allowToAction(VehicleRegisterAction.VEHICLE_REGISTER_DETAIL_ACCESS)
    ) {
      const dialog = this.vehicleRegisterDetailVMFactory(item);
      this.openItem = item;
      this.tryActivateChild(dialog);
    }
  }

  @action.bound
  allowToVehicleSynchronization(item: VehicleRegisterVehicle) {
    return item.permittedActions.vehicleSynchronization.isAllowed;
  }

  @action.bound
  onVehicleSynchronizationClick(item: VehicleRegisterVehicle, isNextStep = false) {
    if (this.allowToVehicleSynchronization(item) && this.allowToAction(VehicleRegisterAction.VEHICLE_SYNCHRONIZATION)) {
      const dialog = this.vehicleSynchronizationVMFactory(item, isNextStep);
      this.openItem = item;
      this.tryActivateChild(dialog);
    }
  }

  disabledActionTooltip(action: VehicleRegisterAction) {
    return this.translate(`actions.${camelCase(action)}.tooltip`);
  }

  translateVehicleStatus(status: VehicleRegisterVehicleStatus) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleStatus", status);
  }

  translateVehicleRegisterVehicleClassifiedFuelType(classifiedFuelType: VehicleRegisterVehicleClassifiedFuelType) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleClassifiedFuelType", classifiedFuelType);
  }

  formatHeight(amount: number) {
    return `${amount} kg`;
  }
}
